/**
 * SHEEP MADE SIMPLE
 * Winter Seasonal Definition
 * Version: 1.0.0
 */
(() => {
  "use strict";

  const SEASON = "winter";
  const TASKS = Object.freeze([
    "winterPaddock",
    "lambingPaddock",
    "grazingRotation",
    "lambingPreparation",
    "earlyLambing"
  ]);

  const required = value => value !== undefined && value !== null && value !== "";
  const valid = errors => ({ valid: errors.length === 0, errors });

  function validators() {
    return {
      winterPaddock(data) {
        return valid(required(data.paddockId) ? [] : ["Choose your winter paddock before saving."]);
      },
      lambingPaddock(data) {
        const errors = [];
        if (!["applicable", "not_applicable"].includes(data.applicability)) {
          errors.push("Choose a lambing paddock or mark this task as not applicable.");
        }
        if (data.applicability === "applicable" && !required(data.paddockId)) {
          errors.push("Choose your lambing paddock before saving.");
        }
        return valid(errors);
      },
      grazingRotation(data) {
        const errors = [];
        if (!Number.isInteger(data.plannedRotationWeeks) || data.plannedRotationWeeks < 1) {
          errors.push("Choose your planned full winter rotation.");
        }
        if (!Number.isInteger(data.plannedGrazingShifts) || data.plannedGrazingShifts < 1) {
          errors.push("Enter the number of planned grazing shifts.");
        }
        return valid(errors);
      },
      lambingPreparation(data) {
        const errors = [];
        if (!["applicable", "not_applicable"].includes(data.applicability)) {
          errors.push("Review lambing preparation or mark it not applicable.");
        }
        if (
          data.applicability === "applicable" &&
          !data.highRiskEwesReviewed &&
          !data.suppliesPrepared &&
          !data.helpPlanReady
        ) {
          errors.push("Select at least one lambing preparation item.");
        }
        return valid(errors);
      },
      earlyLambing(data) {
        const errors = [];
        if (!["applicable", "not_applicable"].includes(data.applicability)) {
          errors.push("Choose your early-lambing status.");
        }
        if (data.applicability === "applicable" && !required(data.dailyChecks)) {
          errors.push("Choose how often you will check the flock.");
        }
        return valid(errors);
      }
    };
  }

  function normaliseTaskData({ taskId, data }) {
    if (taskId === "winterPaddock") {
      return {
        paddockId: data.paddockId || null,
        notes: String(data.notes || "").trim()
      };
    }

    if (taskId === "lambingPaddock") {
      return data.applicability === "not_applicable"
        ? {
            applicability: "not_applicable",
            paddockId: null,
            notes: String(data.notes || "").trim()
          }
        : {
            applicability: "applicable",
            paddockId: data.paddockId || null,
            notes: String(data.notes || "").trim()
          };
    }

    if (taskId === "grazingRotation") {
      return {
        plannedRotationWeeks: Number(data.plannedRotationWeeks) || null,
        plannedGrazingShifts: Number(data.plannedGrazingShifts) || null
      };
    }

    if (taskId === "lambingPreparation") {
      return data.applicability === "not_applicable"
        ? {
            applicability: "not_applicable",
            highRiskEwesReviewed: false,
            suppliesPrepared: false,
            helpPlanReady: false
          }
        : {
            applicability: "applicable",
            highRiskEwesReviewed: Boolean(data.highRiskEwesReviewed),
            suppliesPrepared: Boolean(data.suppliesPrepared),
            helpPlanReady: Boolean(data.helpPlanReady)
          };
    }

    if (taskId === "earlyLambing") {
      return data.applicability === "not_applicable"
        ? {
            applicability: "not_applicable",
            dailyChecks: null,
            newbornFeedingKnowledge: "not_applicable",
            severeWeatherPlan: "not_applicable"
          }
        : {
            applicability: "applicable",
            dailyChecks: data.dailyChecks || null,
            newbornFeedingKnowledge:
              data.newbornFeedingKnowledge === "reviewed" ? "reviewed" : "not_reviewed",
            severeWeatherPlan:
              data.severeWeatherPlan === "prepared" ? "prepared" : "not_prepared"
          };
    }

    return data;
  }

  function registerWinterDefinition() {
    const engine = window.SMSSeasonalEngine;

    if (
      !engine ||
      typeof engine.getDefinition !== "function" ||
      typeof engine.registerSeason !== "function"
    ) {
      window.setTimeout(registerWinterDefinition, 50);
      return;
    }

    if (!engine.getDefinition(SEASON)) {
      engine.registerSeason({
        season: SEASON,
        taskOrder: TASKS,
        validators: validators(),
        normaliseTaskData
      });
    }
  }

  registerWinterDefinition();
})();
